import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router";
import Moment from "react-moment";
import { List as RWList } from "react-window";
import Loading from "../Common/Loading";
import { STATUSES, statusLabel } from "./geofences";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faChevronDown, faChevronUp, faMapMarkedAlt, faSearch } from "@fortawesome/free-solid-svg-icons";
import "./WaylensEventList.css";

const STORAGE_KEY = "waylens.eventList.filters.v4";
const EVENT_OPTIONS = ["", "AGMT_Piezo", "AGMT_Mic", "AGMT_MicPiezo"];
const ORDER_BY = {
    RECEIVED_DATE_TIME: "ReceivedDateTime",
    EVENT_DATE_TIME: "EventDateTime",
};
const ORDER_BY_OPTIONS = [
    { value: ORDER_BY.RECEIVED_DATE_TIME, label: "Received" },
    { value: ORDER_BY.EVENT_DATE_TIME, label: "Event" },
];
const PAIRING_STATUS_OPTIONS = [
    { value: "", label: "All" },
    { value: "Match", label: "Match" },
    { value: "Mismatch", label: "Mismatch" },
    { value: "Unknown", label: "Unknown" },
];

const ROW_HEIGHT = 80;
const OVERSCAN = 8;
const TABLE_GRID_TEMPLATE = [
    "60px",
    "minmax(120px, 1fr)",
    "minmax(150px, 1.15fr)",
    "minmax(155px, 1.1fr)",
    "68px",
    "125px",
    "90px",
    "minmax(210px, 1.3fr)",
    "minmax(174px, 1.25fr)",
    "minmax(174px, 1.25fr)",
].join(" ");
const TABLE_MIN_WIDTH = 1350;

const THEME = {
    pageBg: "#0b0f14",
    panelBg: "#0e141c",
    headerBg: "#0b0f14",
    border: "rgba(255,255,255,0.12)",
    rowA: "rgba(255,255,255,0.02)",
    rowB: "rgba(255,255,255,0.00)",
    hover: "#094f7e",
    matchRow: "rgba(46,160,67,0.14)",
    matchHover: "rgba(46,160,67,0.25)",
    matchAccent: "#3fb950",
    mismatchRow: "rgba(248,81,73,0.14)",
    mismatchHover: "rgba(248,81,73,0.25)",
    mismatchAccent: "#f85149",
    text: "#e6edf3",
    subText: "rgba(230,237,243,0.72)",
};

function safeJsonParse(str) {
    try {
        return JSON.parse(str);
    } catch {
        return null;
    }
}

function normalizeDraft(draft) {
    const trimmedId = (draft.id ?? "").trim();
    const parsedId = Number(trimmedId);

    const icxMacAddress = (draft.icxMacAddress ?? "").trim();

    const rawIcxEvent = (draft.eventIcx ?? "").trim();
    const pairingStatus = PAIRING_STATUS_OPTIONS.some((option) => option.value === draft.pairingStatus)
        ? draft.pairingStatus
        : "";

    let eventIcx = "";
    if (rawIcxEvent.length > 0) {
        const n = Number.parseInt(rawIcxEvent, 10);
        if (Number.isInteger(n) && n >= 0 && n <= 255) {
            eventIcx = String(n);
        }
    }

    return {
        id: Number.isFinite(parsedId) && parsedId > 0 ? String(parsedId) : "",
        deviceSerialNumber: (draft.deviceSerialNumber ?? "").trim(),
        event: (draft.event ?? "").trim(),
        icxMacAddress,
        eventIcx,
        pairingStatus,
        geofenceStatus: STATUSES.includes(draft.geofenceStatus) ? draft.geofenceStatus : "",
        geofenceId: /^[1-9]\d*$/.test(draft.geofenceId ?? "") ? String(draft.geofenceId) : "",
        orderBy: draft.orderBy === ORDER_BY.EVENT_DATE_TIME
            ? ORDER_BY.EVENT_DATE_TIME
            : ORDER_BY.RECEIVED_DATE_TIME,
    };
}


function DateCell({ value }) {
    if (!value) return <span style={{ color: THEME.subText }}>-</span>;

    return (
        <div style={{ lineHeight: 1.15 }}>
            <div style={{ color: THEME.text, fontWeight: 600 }}>
                <Moment fromNow>{value}</Moment>
            </div>
            <div style={{ fontSize: "0.82rem", color: THEME.subText }}>
                <Moment format="YYYY-MM-DD HH:mm:ss">{value}</Moment>
            </div>
        </div>
    );
}

function TextCell({ value }) {
    if (value === null || value === undefined || value === "")
        return <span style={{ color: THEME.subText }}>-</span>;
    return <span>{value}</span>;
}

function PairingStatusBadge({ isMatch }) {
    const status = isMatch === true
        ? { label: "Match", bg: "success" }
        : isMatch === false
            ? { label: "Mismatch", bg: "danger" }
            : { label: "Unknown", bg: "secondary" };

    return (
        <Badge bg={status.bg} style={{ fontSize: "0.80rem", fontWeight: 700 }}>
            {status.label}
        </Badge>
    );
}

function buildQueryString({ appliedFilters, pageNumber, pageSize }) {
    const params = new URLSearchParams();

    params.set("PagingRequest.PageNumber", String(pageNumber));
    params.set("PagingRequest.PageSize", String(pageSize));
    params.set("OrderBy", appliedFilters.orderBy ?? ORDER_BY.RECEIVED_DATE_TIME);

    if (appliedFilters.id) params.set("Id", appliedFilters.id);
    if (appliedFilters.deviceSerialNumber) params.set("DeviceSerialNumber", appliedFilters.deviceSerialNumber);
    if (appliedFilters.event) params.set("Event", appliedFilters.event);
    if (appliedFilters.icxMacAddress) params.set("IcxMacAddress", appliedFilters.icxMacAddress);
    if (appliedFilters.eventIcx) params.set("EventIcx", appliedFilters.eventIcx);
    if (appliedFilters.pairingStatus) params.set("PairingStatus", appliedFilters.pairingStatus);
    if (appliedFilters.geofenceStatus) params.set("GeofenceStatus", appliedFilters.geofenceStatus);
    if (appliedFilters.geofenceId) params.set("GeofenceId", appliedFilters.geofenceId);

    return params.toString();
}

function WaylensRowComponent(props) {
    const { index, style, rows, hasMore, isLoadingMore, onSelect } = props;

    if (index >= rows.length) {
        return (
            <div
                style={{
                    ...style,
                    display: "flex",
                    alignItems: "center",
                    padding: "0 12px",
                    boxSizing: "border-box",
                    minWidth: TABLE_MIN_WIDTH,
                    borderBottom: `1px solid ${THEME.border}`,
                    background: THEME.panelBg,
                    color: THEME.subText,
                }}
            >
                {hasMore ? (
                    <span>{isLoadingMore ? "Loading more…" : "Scroll to load more…"}</span>
                ) : (
                    <span>End of results.</span>
                )}
            </div>
        );
    }

    const row = rows[index];
    const defaultBg = index % 2 === 0 ? THEME.rowA : THEME.rowB;
    const baseBg = row.isIcxCameraPairMatch === true
        ? THEME.matchRow
        : row.isIcxCameraPairMatch === false
            ? THEME.mismatchRow
            : defaultBg;
    const hoverBg = row.isIcxCameraPairMatch === true
        ? THEME.matchHover
        : row.isIcxCameraPairMatch === false
            ? THEME.mismatchHover
            : THEME.hover;
    const statusAccent = row.isIcxCameraPairMatch === true
        ? THEME.matchAccent
        : row.isIcxCameraPairMatch === false
            ? THEME.mismatchAccent
            : null;

    return (
        <div
            style={{
                ...style,
                display: "grid",
                gridTemplateColumns: TABLE_GRID_TEMPLATE,
                alignItems: "center",
                padding: "0 12px",
                boxSizing: "border-box",
                minWidth: TABLE_MIN_WIDTH,
                cursor: "pointer",
                borderBottom: `1px solid ${THEME.border}`,
                backgroundColor: baseBg,
                boxShadow: statusAccent ? `inset 3px 0 0 ${statusAccent}` : "none",
                color: THEME.text,
                fontSize: "0.94rem",
            }}
            onClick={() => onSelect(row)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onSelect(row);
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = hoverBg)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = baseBg)}
        >
            <div style={{ fontWeight: 700 }}>{row.id}</div>

            <div className="text-truncate pe-3" title={row.deviceSerialNumber ?? undefined}>
                {row.deviceSerialNumber ? row.deviceSerialNumber : <span style={{ color: THEME.subText }}>-</span>}
            </div>

            <div className="text-truncate pe-3" title={row.event ?? undefined}>
                {row.event ? (
                    <>
                        {row.event}
                        {row.eventIcx !== null && row.eventIcx !== undefined ? ` (${row.eventIcx})` : ""}
                    </>
                ) : (
                    <TextCell
                        value={row.eventIcx !== null && row.eventIcx !== undefined ? `(${row.eventIcx})` : null}
                    />
                )}
            </div>

            <div className="text-truncate pe-3" title={row.icxMacAddress ?? undefined}>
                <TextCell value={row.icxMacAddress} />
            </div>

            <div className="text-truncate pe-3">
                <TextCell value={row.icxEventTypeCount} />
            </div>

            <div className="text-truncate pe-3">
                <TextCell value={row.icxSecondsSinceEventDetected} />
            </div>

            <div className="pe-2">
                <PairingStatusBadge isMatch={row.isIcxCameraPairMatch} />
            </div>

            <div className="pe-3" style={{ minWidth: 0 }}>
                <Badge bg={row.geofenceStatus === "Inside" ? "info" : "secondary"}>{statusLabel(row.geofenceStatus)}</Badge>
                <div className="text-truncate" style={{ fontSize: "0.82rem" }}
                    title={(row.geofenceMatches || []).map(m => `${m.labelSnapshot} (v${m.geofenceVersion})`).join(", ")}>
                    {(row.geofenceMatches || []).map(m => `${m.labelSnapshot} (v${m.geofenceVersion})`).join(", ") || "—"}
                </div>
            </div>

            <div className="pe-3">
                <DateCell value={row.eventTimeUtc} />
            </div>

            <div>
                <DateCell value={row.receivedUtc} />
            </div>
        </div>
    );
}

export const WaylensEventList = ({ apiFetch }) => {
    document.title = "AGMT | Waylens Events";

    const navigate = useNavigate();
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [geofences, setGeofences] = useState([]);
    const [historicalGeofences, setHistoricalGeofences] = useState([]);
    const [geofenceError, setGeofenceError] = useState("");
    useEffect(() => {
        let active = true;
        apiFetch("/api/Waylens/geofences").then(async response => {
            if (!response.ok) throw new Error("Unable to load geofence filter locations.");
            const locations = await response.json();
            if (active) setGeofences(locations);
        }).catch(error => { if (active) setGeofenceError(error.message); });
        apiFetch("/api/Waylens/geofence-match-options").then(async response => {
            if (!response.ok) throw new Error("Unable to load historical geofence filter locations.");
            const options = await response.json();
            if (active) setHistoricalGeofences(options);
        }).catch(error => { if (active) setGeofenceError(error.message); });
        return () => { active = false; };
    }, [apiFetch]);

    const initial = useMemo(() => {
        const stored = safeJsonParse(localStorage.getItem(STORAGE_KEY));
        const fallback = {
            id: "",
            deviceSerialNumber: "",
            event: "",
            icxMacAddress: "",
            eventIcx: "",
            pairingStatus: "",
            orderBy: ORDER_BY.RECEIVED_DATE_TIME,
        };
        return normalizeDraft(stored ? { ...fallback, ...stored } : fallback);
    }, []);

    const [draft, setDraft] = useState(initial);
    const [applied, setApplied] = useState(normalizeDraft(initial));

    const [rows, setRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize] = useState(100);

    const [hasMore, setHasMore] = useState(true);
    const [fetchingFirstPage, setFetchingFirstPage] = useState(false);
    const [fetchingMore, setFetchingMore] = useState(false);
    const [error, setError] = useState("");

    const [listHeight, setListHeight] = useState(600);

    useEffect(() => {
        const recompute = () => {
            const px = Math.max(280, window.innerHeight - 300);
            setListHeight(px);
        };
        recompute();
        window.addEventListener("resize", recompute);
        return () => window.removeEventListener("resize", recompute);
    }, []);

    const inFlightRef = useRef(null);

    const activeFilters = useMemo(() => {
        const items = [];
        if (applied.id) items.push({ k: "Id", v: applied.id });
        if (applied.deviceSerialNumber) items.push({ k: "Serial", v: applied.deviceSerialNumber });
        if (applied.event) items.push({ k: "Event", v: applied.event });
        if (applied.icxMacAddress) items.push({ k: "ICx MAC", v: applied.icxMacAddress });
        if (applied.eventIcx) items.push({ k: "ICx Event", v: applied.eventIcx });
        if (applied.pairingStatus) items.push({ k: "Pairing", v: applied.pairingStatus });
        if (applied.geofenceStatus) items.push({ k: "Geofence", v: statusLabel(applied.geofenceStatus) });
        if (applied.geofenceId) items.push({ k: "Location ID", v: applied.geofenceId });

        return items;
    }, [applied]);


    const persistApplied = useCallback((filters) => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    }, []);

    const fetchPage = useCallback(
        async ({ nextPageNumber, mode }) => {
            if (inFlightRef.current && mode === "append") return;
            inFlightRef.current?.abort();
            const controller = new AbortController();
            inFlightRef.current = controller;

            setError("");

            try {
                const query = buildQueryString({
                    appliedFilters: applied,
                    pageNumber: nextPageNumber,
                    pageSize,
                });

                const url = `/api/Waylens/GetWaylensEvents?${query}`;
                const resp = await apiFetch(url, { signal: controller.signal });
                if (controller.signal.aborted) return;

                if (!resp.ok) {
                    setError(`Failed to load Waylens events. Status ${resp.status}`);
                    return;
                }

                const payload = await resp.json();
                if (controller.signal.aborted) return;
                const newRows = payload?.results ?? [];
                const more = payload?.pageDetail?.hasMoreData ?? false;

                if (mode === "replace") setRows(newRows);
                else setRows((prev) => [...prev, ...newRows]);

                setHasMore(Boolean(more));
                setPageNumber(nextPageNumber);
            } catch (e) {
                if (controller.signal.aborted) return;
                // eslint-disable-next-line no-console
                console.error(e);
                setError("Failed to load events due to a network or server error.");
            } finally {
                if (inFlightRef.current === controller) {
                    inFlightRef.current = null;
                    setFetchingFirstPage(false);
                    setFetchingMore(false);
                }
            }
        },
        [apiFetch, applied, pageSize]
    );

    useEffect(() => {
        setRows([]);
        setHasMore(true);
        setPageNumber(1);

        setFetchingFirstPage(true);
        fetchPage({ nextPageNumber: 1, mode: "replace" });
        return () => {
            inFlightRef.current?.abort();
            inFlightRef.current = null;
        };
    }, [applied, fetchPage]);

    const applyFilters = useCallback(() => {
        const normalized = normalizeDraft(draft);
        setApplied(normalized);
        persistApplied(normalized);
    }, [draft, persistApplied]);

    const clearFilters = useCallback(() => {
        const cleared = {
            geofenceStatus: "",
            geofenceId: "",
            id: "",
            deviceSerialNumber: "",
            event: "",
            icxMacAddress: "",
            eventIcx: "",
            pairingStatus: "",
            orderBy: applied.orderBy ?? ORDER_BY.RECEIVED_DATE_TIME,
        };
        setDraft(cleared);
        setApplied(cleared);
        persistApplied(cleared);
    }, [applied.orderBy, persistApplied]);

    const changeOrderBy = useCallback((orderBy) => {
        const normalizedOrderBy = orderBy === ORDER_BY.EVENT_DATE_TIME
            ? ORDER_BY.EVENT_DATE_TIME
            : ORDER_BY.RECEIVED_DATE_TIME;

        setDraft((previousDraft) => ({
            ...previousDraft,
            orderBy: normalizedOrderBy,
        }));

        setApplied((previousApplied) => {
            const nextApplied = {
                ...previousApplied,
                orderBy: normalizedOrderBy,
            };
            persistApplied(nextApplied);
            return nextApplied;
        });
    }, [persistApplied]);

    const onSelectRow = useCallback(
        (row) => navigate(`/waylens/event/${row.id}`),
        [navigate]
    );

    const onRowsRendered = useCallback(
        (visibleRows) => {
            if (!hasMore || fetchingMore || fetchingFirstPage) return;

            const stopIndex = visibleRows?.stopIndex ?? 0;
            const threshold = rows.length - 1 - 8;

            if (stopIndex >= threshold && rows.length > 0) {
                setFetchingMore(true);
                fetchPage({ nextPageNumber: pageNumber + 1, mode: "append" });
            }
        },
        [hasMore, fetchingMore, fetchingFirstPage, rows.length, fetchPage, pageNumber]
    );

    const rowCount = useMemo(() => rows.length + (hasMore ? 1 : 0), [rows.length, hasMore]);

    const panelStyle = {
        background: THEME.panelBg,
        border: `1px solid ${THEME.border}`,
        borderRadius: 8,
    };

    const locationOptions = [
        { value: "", label: "All locations" },
        ...geofences.map(g => ({ value: String(g.id), label: g.label + (g.isEnabled ? "" : " (disabled)") })),
        ...historicalGeofences.map(m => ({
            value: String(m.geofenceId), label: `${m.labelSnapshot} (saved v${m.geofenceVersion}, #${m.geofenceId})`,
        })),
    ];
    if (draft.geofenceId && !locationOptions.some(option => option.value === draft.geofenceId))
        locationOptions.push({ value: draft.geofenceId, label: `Historical location #${draft.geofenceId}` });

    const renderFilter = (key, label, options = null, placeholder = "") => (
        <Form.Group controlId={`waylens-filter-${key}`}>
            <Form.Label>{label}</Form.Label>
            <Form.Control as={options ? "select" : "input"} value={draft[key]} placeholder={placeholder}
                inputMode={key === "eventIcx" || key === "id" ? "numeric" : undefined}
                onChange={e => setDraft(previous => ({ ...previous, [key]: e.target.value }))}>
                {options ? options.map((option, index) => (
                    <option key={`${option.value}-${index}`} value={option.value}>{option.label}</option>
                )) : undefined}
            </Form.Control>
        </Form.Group>
    );

    return (
        <div className="webcontainer" style={{ background: THEME.pageBg, minHeight: "100vh" }}>
            <Container fluid>
                <Row className="mb-2">
                    <Col md={12} className="fixed-col2">
                        <div className="contentvehicle3">
                            <h4 className="text-light mb-0">Waylens Events</h4>
                        </div>
                    </Col>
                </Row>

                <div className="waylens-results-toolbar">
                    <Button variant={filtersOpen ? "info" : "outline-light"} onClick={() => setFiltersOpen(open => !open)}
                        aria-expanded={filtersOpen} aria-controls="waylens-filters">
                        <FontAwesomeIcon icon={faFilter} className="mr-2" />
                        Filters{activeFilters.length ? ` (${activeFilters.length})` : ""}
                        <FontAwesomeIcon icon={filtersOpen ? faChevronUp : faChevronDown} className="ml-2" />
                    </Button>
                    <span className="waylens-filter-summary">
                        {activeFilters.length ? activeFilters.map(f => `${f.k}: ${f.v}`).join(" · ") : "All events"}
                    </span>
                    <div className="waylens-results-actions">
                        <label htmlFor="waylens-order" className="mb-0">Order by</label>
                        <Form.Control id="waylens-order" as="select" value={draft.orderBy}
                            onChange={e => changeOrderBy(e.target.value)} className="bg-dark text-light">
                            {ORDER_BY_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </Form.Control>
                        <Button variant="outline-light" onClick={() => navigate("/waylens/geofences")}>
                            <FontAwesomeIcon icon={faMapMarkedAlt} className="mr-2" />Locations
                        </Button>
                    </div>
                </div>
                {filtersOpen && (
                    <Form id="waylens-filters" className="waylens-filter-panel" onSubmit={e => { e.preventDefault(); applyFilters(); }}>
                        {geofenceError && <div className="alert alert-warning">{geofenceError}</div>}
                        <div className="waylens-filter-groups">
                            <fieldset>
                                <legend>Event &amp; Camera</legend>
                                {renderFilter("id", "Event ID", null, "e.g. 12345")}
                                {renderFilter("deviceSerialNumber", "Camera SN", null, "Exact serial")}
                                {renderFilter("event", "Event Type", EVENT_OPTIONS.map(value => ({ value, label: value || "All types" })))}
                            </fieldset>
                            <fieldset>
                                <legend>ICX</legend>
                                {renderFilter("icxMacAddress", "MAC Address", null, "AA:BB:CC:DD:EE:FF")}
                                {renderFilter("eventIcx", "Event Type", null, "e.g. 9")}
                                {renderFilter("pairingStatus", "Pairing Status", PAIRING_STATUS_OPTIONS)}
                            </fieldset>
                            <fieldset>
                                <legend>Geofencing</legend>
                                {renderFilter("geofenceStatus", "Status", [
                                    { value: "", label: "All statuses" },
                                    ...STATUSES.map(value => ({ value, label: statusLabel(value) })),
                                ])}
                                {renderFilter("geofenceId", "Matched Location", locationOptions)}
                            </fieldset>
                        </div>
                        <div className="waylens-filter-footer">
                            <Button variant="outline-light" onClick={clearFilters}>Clear</Button>
                            <Button variant="info" type="submit"><FontAwesomeIcon icon={faSearch} className="mr-2" />Search</Button>
                        </div>
                    </Form>
                )}
                <Row>
                    <Col md={12}>
                        {fetchingFirstPage && <Loading />}

                        {!fetchingFirstPage && error && <div className="alert alert-danger mb-3">{error}</div>}

                        {!fetchingFirstPage && !error && (
                            <div style={{ ...panelStyle, overflowX: "auto", overflowY: "hidden" }}>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: TABLE_GRID_TEMPLATE,
                                        minWidth: TABLE_MIN_WIDTH,
                                        width: "100%",
                                        boxSizing: "border-box",
                                        alignItems: "center",
                                        padding: "10px 12px",
                                        borderBottom: `1px solid ${THEME.border}`,
                                        background: THEME.headerBg,
                                        color: THEME.text,
                                        fontSize: "0.88rem",
                                        fontWeight: 600,
                                    }}
                                >
                                    <div>Id</div>
                                    <div className="pe-3">
                                        Camera SN
                                    </div>
                                    <div className="pe-3">
                                        Event Type
                                    </div>
                                    <div className="pe-3">ICX MAC</div>
                                    <div className="pe-3">ICX Count</div>
                                    <div className="pe-3">Seconds Since Crash (s)</div>
                                    <div className="pe-2">Pairing</div>
                                    <div className="pe-3">Geofencing / Saved Locations</div>
                                    <div className="pe-3">
                                        Event Time (UTC)
                                    </div>
                                    <div>Received (UTC)</div>
                                </div>

                                {rows.length === 0 ? (
                                    <div className="p-4" style={{ color: THEME.text }}>
                                        No results found.
                                    </div>
                                ) : (
                                    <RWList
                                        rowComponent={WaylensRowComponent}
                                        rowCount={rowCount}
                                        rowHeight={ROW_HEIGHT}
                                        rowProps={{
                                            rows,
                                            hasMore,
                                            isLoadingMore: fetchingMore,
                                            onSelect: onSelectRow,
                                        }}
                                        overscanCount={OVERSCAN}
                                        onRowsRendered={onRowsRendered}
                                        style={{ height: listHeight, minWidth: TABLE_MIN_WIDTH, width: "100%" }}
                                    />
                                )}
                            </div>
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};
